/**
 * Execution Control Center — the screen's constants and pure logic.
 *
 * Cloned from the `<script>` block of
 * `/Users/glucic/src/ai_readiness_template/views/roadmap-execution/tracker.ejs`.
 *
 * SPLIT OUT OF THE COMPONENT, not redesigned. The source keeps these as free functions in
 * one file; here they are a module so the render code beside them stays readable and so the
 * arithmetic can be tested against the source directly. Every value, threshold and rounding
 * rule is the source's (CLAUDE.md §1.2).
 */

export interface EccDeliverable {
  id: number;
  phase: string;
  name: string;
  owner: string | null;
  document_link: string | null;
  due: string | null;
  progress: number;
  status: string;
  notes?: string | null;
  raci?: unknown;
  work_item_type?: string;
  /** Remembered so unticking Done restores the progress it had, not 0. */
  _prev?: number;
}

export interface EccEvent {
  id: number;
  title: string;
  event_type: string;
  event_date: string | null;
  status: string;
  notes: string | null;
  recurrence: string;
  recurrence_end: string | null;
  recurrence_days: string | null;
}

export interface EccRiskNote {
  text?: string;
  author?: string;
  date?: string;
}

export interface EccRisk {
  id: number;
  title: string;
  owner: string | null;
  due_date: string | null;
  mitigation: string | null;
  resolution: string | null;
  status: string;
  severity: string;
  notes: EccRiskNote[];
}

export interface EccPhase {
  key: string;
  label: string;
  mode?: string;
}

export interface EccBounds {
  start: string | null;
  end: string | null;
}

export interface EccSpan {
  opp: string;
  key: string;
  start: string;
  end: string;
}

export interface EccProject {
  delivery_project_id: string;
  description: string | null;
  methodology: string | null;
  finalized?: unknown;
  start_date: string | null;
}

export interface EccData {
  success: boolean;
  error?: string;
  project: EccProject;
  phases: EccPhase[];
  roles: { code: string; label: string }[];
  deliverables: EccDeliverable[];
  events: EccEvent[];
  risks: EccRisk[];
  phase_bounds: Record<string, EccBounds>;
  phase_spans: EccSpan[];
  all_opps: boolean;
  scope_opportunity_id: string | null;
}

export const RISK_STATUS = ['Open', 'Mitigating', 'Resolved', 'Closed'];
export const RISK_SEV = ['Low', 'Medium', 'High', 'Critical'];
export const SEV_COLOR: Record<string, string> = {
  Low: '#43a047',
  Medium: '#f9a825',
  High: '#fb8c00',
  Critical: '#e53935',
};

export const PHASE_LABEL: Record<string, string> = {
  Assessment_Planning: 'Assessment & Planning',
  Design_Architecture: 'Design & Architecture',
  Development: 'Development',
  Testing_Validation: 'Testing & Validation',
  GoLive_Optimization: 'Go-Live & Optimization',
  Agile_Delivery: 'Agile Delivery',
  Global: 'Global / Shared',
};
export const PHASE_COLORS: Record<string, string> = {
  Assessment_Planning: '#42a5f5',
  Design_Architecture: '#ab47bc',
  Development: '#66bb6a',
  Testing_Validation: '#ffa726',
  GoLive_Optimization: '#ef5350',
  Agile_Delivery: '#26a69a',
  Global: '#6a1b9a',
};
/** Governance timeline uses a DARKER text colour per phase than the band colour above. */
export const PHASE_TEXT_C: Record<string, string> = {
  Assessment_Planning: '#1e88e5',
  Design_Architecture: '#8e24aa',
  Development: '#2e7d32',
  Testing_Validation: '#ef6c00',
  GoLive_Optimization: '#d32f2f',
};

export const STATUSES = ['Not Started', 'In Progress', 'Blocked', 'Done'];
export const STATUS_COLOR: Record<string, string> = {
  'Not Started': '#9e9e9e',
  'In Progress': '#1976d2',
  Blocked: '#e53935',
  Done: '#43a047',
};
export const STATUS_ICON: Record<string, string> = {
  'Not Started': 'radio_button_unchecked',
  'In Progress': 'pending',
  Blocked: 'block',
  Done: 'check_circle',
};

export const EV_TYPES = ['Meeting', 'Steering Committee', 'Gate Review', 'Milestone', 'Workshop'];
export const EV_COLOR: Record<string, string> = {
  Meeting: '#5e35b1',
  'Steering Committee': '#c62828',
  'Gate Review': '#00838f',
  Milestone: '#ef6c00',
  Workshop: '#2e7d32',
};
export const EV_RECUR = ['One-time', 'Weekly', 'Bi-weekly', 'Monthly', 'Quarterly'];
export const DOW_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
export const DOW_IDX: Record<string, number> = {
  Sun: 0,
  Mon: 1,
  Tue: 2,
  Wed: 3,
  Thu: 4,
  Fri: 5,
  Sat: 6,
};
export const MON3 = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

export function today(): Date {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

/** Parse `YYYY-MM-DD` as a LOCAL date. `new Date(s)` would read it as UTC and shift a day. */
export function pd(s: unknown): Date | null {
  if (!s) return null;
  const p = String(s).split('T')[0]!.split('-').map(Number);
  if (!p[0]) return null;
  return new Date(p[0], p[1]! - 1, p[2]!);
}

export function fmtShort(s: unknown): string {
  const d = pd(s);
  if (!d) return '';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

export function ymd(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/**
 * US federal holidays for a year, memoised.
 *
 * Non-working days are weekends PLUS these — the Gantt shades them and the calendar's phase
 * bands skip them, so a band visibly stops over Thanksgiving rather than running through it.
 * The rules are computed, not tabulated: `nth(month, weekday, n)` and `last(month, weekday)`
 * give MLK Day, Presidents' Day, Memorial Day, Labor Day, Columbus Day and Thanksgiving,
 * with the five fixed dates listed directly.
 */
const _hol: Record<number, Set<string>> = {};
export function holidaySet(year: number): Set<string> {
  if (_hol[year]) return _hol[year]!;
  const s = new Set<string>();
  const nth = (m: number, wd: number, n: number) => {
    const f = new Date(year, m, 1);
    const off = (wd - f.getDay() + 7) % 7;
    return new Date(year, m, 1 + off + (n - 1) * 7);
  };
  const last = (m: number, wd: number) => {
    const ld = new Date(year, m + 1, 0);
    const off = (ld.getDay() - wd + 7) % 7;
    return new Date(year, m, ld.getDate() - off);
  };
  [
    new Date(year, 0, 1),
    nth(0, 1, 3),
    nth(1, 1, 3),
    last(4, 1),
    new Date(year, 5, 19),
    new Date(year, 6, 4),
    nth(8, 1, 1),
    nth(9, 1, 2),
    new Date(year, 10, 11),
    nth(10, 4, 4),
    new Date(year, 11, 25),
  ].forEach((d) => s.add(ymd(d)));
  _hol[year] = s;
  return s;
}

export function isNonWorking(d: Date): boolean {
  const w = d.getDay();
  if (w === 0 || w === 6) return true;
  return holidaySet(d.getFullYear()).has(ymd(d));
}

/**
 * A phase or the whole project is 100% only when EVERY item is 100%; otherwise capped at 99.
 *
 * That cap is deliberate and is the source's: rounding must never let a phase with an
 * unfinished item report "complete".
 */
export function pctOf(ds: { progress?: number }[]): number {
  if (!ds.length) return 0;
  const avg = Math.round(ds.reduce((a, x) => a + (Number(x.progress) || 0), 0) / ds.length);
  const allDone = ds.every((x) => (Number(x.progress) || 0) >= 100);
  return allDone ? 100 : Math.min(avg, 99);
}

/**
 * Progress drives status: 100 = Done, >0 = In Progress, 0 = Not Started.
 * Blocked is the ONE status set by hand, and it is preserved below 100.
 */
export function autoStatus(x: EccDeliverable): void {
  if (x.progress >= 100) {
    x.status = 'Done';
    return;
  }
  if (x.status === 'Blocked') return;
  x.status = x.progress > 0 ? 'In Progress' : 'Not Started';
}

/**
 * Outlook-style recurrence expansion → an array of `YYYY-MM-DD` session dates.
 *
 * Weekly and Bi-weekly walk WEEK BOUNDARIES (the cursor is snapped back to that week's
 * Sunday) so a multi-day selection — Mon and Thu, say — produces both days of every week,
 * which stepping day-by-day from the start date could not. `cap` at 400 is the source's
 * runaway guard, and the result is deduped and sorted because a Monthly series started on
 * the 31st can land on the same clamped date twice.
 */
export function expandOccurrences(e: Partial<EccEvent>): string[] {
  if (!e.event_date) return [];
  const start = pd(e.event_date);
  if (!start) return [];
  const rec = e.recurrence || 'One-time';
  if (rec === 'One-time') return [e.event_date];
  const end = e.recurrence_end ? pd(e.recurrence_end) : null;
  if (!end || end < start) return [e.event_date];
  const days = (e.recurrence_days || '')
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
  const dows = days.length
    ? days.map((d) => DOW_IDX[d]).filter((d): d is number => d != null)
    : [start.getDay()];
  const out: string[] = [];
  const cap = 400;
  if (rec === 'Weekly' || rec === 'Bi-weekly') {
    const step = rec === 'Bi-weekly' ? 2 : 1;
    const wk = new Date(start);
    wk.setDate(wk.getDate() - start.getDay());
    while (wk <= end && out.length < cap) {
      dows.forEach((dw) => {
        const dt = new Date(wk);
        dt.setDate(dt.getDate() + dw);
        if (dt >= start && dt <= end) out.push(ymd(dt));
      });
      wk.setDate(wk.getDate() + 7 * step);
    }
  } else if (rec === 'Monthly') {
    let dt = new Date(start);
    while (dt <= end && out.length < cap) {
      out.push(ymd(dt));
      dt = new Date(dt.getFullYear(), dt.getMonth() + 1, start.getDate());
    }
  } else if (rec === 'Quarterly') {
    let dt = new Date(start);
    while (dt <= end && out.length < cap) {
      out.push(ymd(dt));
      dt = new Date(dt.getFullYear(), dt.getMonth() + 3, start.getDate());
    }
  } else {
    return [e.event_date];
  }
  return [...new Set(out)].sort();
}

/** The left pane's mini-bar and the slider share this gradient rule. */
export function progBgVal(v: number): string {
  const col = v >= 100 ? '#43a047' : v >= 50 ? '#26a69a' : '#42a5f5';
  return `linear-gradient(90deg,#42a5f5,${col}) 0/${v}% 100% no-repeat, #eceff1`;
}

/** Utilization colour bands: over plan red, 85%+ amber, else green. */
export function utilColor(p: number): string {
  return p > 100 ? '#e53935' : p >= 85 ? '#fb8c00' : '#43a047';
}
